import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, map, Observable } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { environment } from '@environments/environment';

export enum WebsocketEventType {
  PARAMS_UPDATED = 'PARAMS_UPDATED',
  INVOICES_UPDATED = 'INVOICES_UPDATED',
  INVOICE_ADDED = 'INVOICE_ADDED',
  INVOICE_SENT = 'INVOICE_SENT',
  INVOICE_DELETED = 'INVOICE_DELETED',
  ALERTS_UPDATED = 'ALERTS_UPDATED',
  CONTRACTS_UPDATED = 'CONTRACTS_UPDATED',
}

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  private socket!: WebSocket;
  private listener: BehaviorSubject<any> = new BehaviorSubject(null);
  private idToken: string | null = '';
  private reconnectTimeout?: any;

  constructor(private authService: AuthService) {}

  /** Connexion au WebSocket */
  public connect(): BehaviorSubject<any> {
    if (!this.socket || this.socket.readyState === WebSocket.CLOSED) {
      this.create();
    }
    return this.listener;
  }

  /** Crée la connexion WebSocket et configure les handlers */
  private create() {
    this.idToken = this.authService.getToken();
    if (!this.idToken) {
      console.error('No auth token, cannot connect WebSocket.');
      return;
    }

    this.socket = new WebSocket(
      `wss://${environment.WEBSOCKET_ENDPOINT}.execute-api.eu-central-1.amazonaws.com/v1?Auth=${this.idToken}`,
    );

    this.socket.onopen = () => {
      console.log('WebSocket connection established.');
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('WebSocket message received:', data);

        if (data?.message === 'Forbidden') {
          console.warn('WebSocket connection forbidden, reconnecting...');
          this.reconnect();
          return;
        }

        this.listener.next(data);
      } catch {
        this.listener.next(event.data);
      }
    };

    this.socket.onclose = (event) => {
      console.log('WebSocket connection closed:', event);

      if (event.code !== 1000) {
        console.log('Attempting to reconnect WebSocket in 2s...');
        this.reconnectTimeout = setTimeout(() => this.create(), 2000);
      }

      if (event.code === 1008) {
        console.error('Unauthorized access - 401');
      }
    };

    this.socket.onerror = (error: unknown) => {
      console.error('WebSocket connection error:', error);
    };
  }

  /** Fermer la connexion */
  public close() {
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    if (this.socket) {
      this.socket.close(1000, 'Normal closure');
    }
  }

  /** Reconnect automatique */
  private reconnect() {
    console.log('Reconnecting WebSocket...');
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
      this.socket.close(1000, 'Reconnect');
    }
    this.reconnectTimeout = setTimeout(() => this.create(), 2000);
  }

  /** Observable filtré pour les PARAMS_UPDATED */
  public onParamsUpdated(): Observable<{ newItem: any; oldItem: any }> {
    return this.listener.asObservable().pipe(
      filter((msg) => msg?.type === WebsocketEventType.PARAMS_UPDATED),
      map((msg) => ({ newItem: msg.newItem, oldItem: msg.oldItem })),
    );
  }

  /** Observable filtré pour les INVOICES_UPDATED */
  public onInvoicesUpdated(): Observable<{ newItem: any; oldItem: any; type: WebsocketEventType }> {
    const invoiceEvents = new Set([
      WebsocketEventType.INVOICES_UPDATED,
      WebsocketEventType.INVOICE_ADDED,
      WebsocketEventType.INVOICE_DELETED,
      WebsocketEventType.INVOICE_SENT,
    ]);
    return this.listener.asObservable().pipe(
      filter((msg) => invoiceEvents.has(msg?.type)),
      map((msg) => ({ newItem: msg.newItem, oldItem: msg.oldItem, type: msg.type })),
    );
  }

  /** Observable filtré pour les ALERTS_UPDATED */
  public onAlertsUpdated(): Observable<{ newItem: any; oldItem: any }> {
    return this.listener.asObservable().pipe(
      filter((msg) => msg?.type === WebsocketEventType.ALERTS_UPDATED),
      map((msg) => ({ newItem: msg.newItem, oldItem: msg.oldItem })),
    );
  }

  /** Observable filtré pour les CONTRACTS_UPDATED */
  public onContractsUpdated(): Observable<{ newItem: any; oldItem: any }> {
    return this.listener.asObservable().pipe(
      filter((msg) => msg?.type === WebsocketEventType.CONTRACTS_UPDATED),
      map((msg) => ({ newItem: msg.newItem, oldItem: msg.oldItem })),
    );
  }
}
